import { useState } from "react";
import { useTracking } from "react-tracking";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import {
  BaseSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../../shared/components/select/BaseSelect";
import {
  Code,
  Database,
  Download,
  FileSpreadsheet,
  FileText,
} from "lucide-react";
import { Button } from "../../../../../../shared/components/button/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../../shared/components/table/Table";
import useFileDownloader from "../../../../../../hooks/useFileDownloader";
import useLoading from "../../../../../../shared/components/loading/useLoading";
import {
  API_DATASET_DOWNLOAD,
  API_DATASET_DOWNLOAD_POWERBI,
  API_DATASET_DOWNLOAD_PYTHON,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import {
  catchFunction,
  groupDataLibraryFilters,
} from "../../../../../../shared/lib/utils";

const ResultsTable = ({
  data,
  isLoading,
  datasetCode,
  selectedDimensions,
  selectedMetrics,
  filters,
}) => {
  const fileDownloader = useFileDownloader();
  const { setLoading } = useLoading();
  const { trackEvent } = useTracking();
  // ********************************************************
  const [exportFormat, setExportFormat] = useState("");
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  const exportData = (format) => {
    if (data.length === 0) return;

    setLoading(true);

    const request = {
      dimensions: selectedDimensions,
      metrics: selectedMetrics,
      filters: groupDataLibraryFilters(filters).map((item) => {
        return {
          dimension: item.dimension.code,
          operation: item.operation.code,
          values: item.value,
        };
      }),
    };

    trackEvent({
      page: "datasets-library-lens",
      event: "button-clicked",
      data: {
        name: "export-format-button",
        format: format,
        dataset: datasetCode,
        request: request,
      },
    });

    switch (format) {
      case "csv-es":
        fileDownloader
          .download(
            `${API_DATASET_DOWNLOAD(datasetCode, "es", "csv")}`,
            request,
          )
          .catch(catchFunction)
          .finally(() => setLoading(false));
        break;
      case "csv-uk":
        fileDownloader
          .download(
            `${API_DATASET_DOWNLOAD(datasetCode, "en", "csv")}`,
            request,
          )
          .catch(catchFunction)
          .finally(() => setLoading(false));
        break;
      case "json":
        fileDownloader
          .download(
            `${API_DATASET_DOWNLOAD(datasetCode, "es", "json")}`,
            request,
          )
          .catch(catchFunction)
          .finally(() => setLoading(false));
        break;
      case "python":
        fileDownloader
          .download(`${API_DATASET_DOWNLOAD_PYTHON(datasetCode)}`, request)
          .catch(catchFunction)
          .finally(() => setLoading(false));
        break;
      case "powerbi":
        fileDownloader
          .download(`${API_DATASET_DOWNLOAD_POWERBI(datasetCode)}`, request)
          .catch(catchFunction)
          .finally(() => setLoading(false));
        break;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Results Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading results...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Results Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">
              No data to display. Please select dimensions/metrics and submit
              your query.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Results Preview (First 100 rows)</CardTitle>
        <div className="flex items-center gap-2">
          <BaseSelect value={exportFormat} onValueChange={setExportFormat}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Export format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv-uk">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  CSV - U.K. format
                </div>
              </SelectItem>
              <SelectItem value="csv-es">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  CSV - Spanish format
                </div>
              </SelectItem>
              <SelectItem value="json">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  JSON
                </div>
              </SelectItem>
              <SelectItem value="powerbi">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  PowerBI
                </div>
              </SelectItem>
              <SelectItem value="python">
                <div className="flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Python
                </div>
              </SelectItem>
            </SelectContent>
          </BaseSelect>
          <Button
            onClick={() => exportData(exportFormat)}
            disabled={!exportFormat}
            variant="outline"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-auto max-h-96">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column} className="whitespace-nowrap">
                    {column}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column} className="whitespace-nowrap">
                      {row[column] + ""}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 text-sm text-muted-foreground">
          Showing {data.length} rows
        </div>
      </CardContent>
    </Card>
  );
};

export default ResultsTable;
